import React, { useState, useEffect } from 'react'
import { Plus, Search, Clock, Users, Star, ChefHat, Filter, Edit, Trash2, Link } from 'lucide-react'
import { apiClient } from '@/utils/api'
import RecipeEditor from './RecipeEditor'
import KioskRecipeViewer from '../kiosk/KioskRecipeViewer'

interface Recipe {
  id: string
  name: string
  description?: string
  photo_url?: string
  meal_type?: string
  prep_time_minutes?: number
  cook_time_minutes?: number
  total_time_minutes?: number
  servings: number
  difficulty: string
  avg_rating?: number
  review_count?: number
  tags?: string[]
  is_favorite?: boolean
}

export default function RecipeList() {
  const [recipes, setRecipes] = useState<Recipe[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<string>('')
  const [filterDifficulty, setFilterDifficulty] = useState<string>('')
  const [showEditor, setShowEditor] = useState(false)
  const [editingRecipeId, setEditingRecipeId] = useState<string | null>(null)
  const [showViewer, setShowViewer] = useState(false)
  const [viewingRecipeId, setViewingRecipeId] = useState<string | null>(null)
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false)

  // Format minutes into hours and minutes
  const formatTime = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes}m`
    }
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    if (mins === 0) {
      return `${hours}h`
    }
    return `${hours}h ${mins}m`
  }

  useEffect(() => {
    loadRecipes()
  }, [filterType, filterDifficulty, showFavoritesOnly])

  const loadRecipes = async () => {
    setLoading(true)
    try {
      const params: any = {}
      if (filterType) params.meal_type = filterType
      if (filterDifficulty) params.difficulty = filterDifficulty
      if (showFavoritesOnly) params.favorites_only = true
      
      const data = await apiClient.getRecipes(params)
      setRecipes(data || [])
    } catch (error) {
      console.error('Failed to load recipes:', error)
      setRecipes([])
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (recipeId: string) => {
    if (!confirm('Are you sure you want to delete this recipe?')) return
    
    try {
      await apiClient.deleteRecipe(recipeId)
      await loadRecipes()
    } catch (error) {
      console.error('Failed to delete recipe:', error)
    }
  }

  const handleToggleFavorite = async (recipeId: string) => {
    try {
      await apiClient.toggleRecipeFavorite(recipeId)
      await loadRecipes()
    } catch (error) {
      console.error('Failed to toggle favorite:', error)
    }
  }

  const handleLinkToMeal = async (recipeId: string) => {
    // This would open a modal to select which meal option to link to
    // For now, just show an alert
    alert('Link to meal option feature coming soon!')
  }

  const filteredRecipes = recipes.filter(recipe => 
    recipe.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    recipe.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    recipe.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  if (showEditor) {
    return (
      <RecipeEditor
        recipeId={editingRecipeId || undefined}
        onSave={() => {
          setShowEditor(false)
          setEditingRecipeId(null)
          loadRecipes()
        }}
        onCancel={() => {
          setShowEditor(false)
          setEditingRecipeId(null)
        }}
      />
    )
  }

  if (showViewer && viewingRecipeId) {
    return (
      <div className="relative">
        <KioskRecipeViewer
          recipeId={viewingRecipeId}
          onClose={() => {
            setShowViewer(false)
            setViewingRecipeId(null)
          }}
          fullScreen={false}
        />
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <ChefHat className="w-6 h-6 text-orange-500" />
          Recipe Collection
        </h2>
        <button
          onClick={() => setShowEditor(true)}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Recipe
        </button>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search recipes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
          />
        </div>
        
        <div className="flex gap-3 flex-wrap">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="">All Types</option>
            <option value="breakfast">Breakfast</option>
            <option value="lunch">Lunch</option>
            <option value="dinner">Dinner</option>
            <option value="snack">Snack</option>
            <option value="dessert">Dessert</option>
          </select>
          
          <select
            value={filterDifficulty}
            onChange={(e) => setFilterDifficulty(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="">All Difficulties</option>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>

          <label className="flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              checked={showFavoritesOnly}
              onChange={(e) => setShowFavoritesOnly(e.target.checked)}
              className="rounded"
            />
            <Star className="w-4 h-4 text-yellow-500" />
            <span>Favorites Only</span>
          </label>
        </div>
      </div>

      {/* Recipe Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : filteredRecipes.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <ChefHat className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No recipes found</p>
          <button
            onClick={() => setShowEditor(true)}
            className="mt-4 text-blue-500 hover:text-blue-600"
          >
            Add your first recipe
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredRecipes.map(recipe => (
            <div
              key={recipe.id}
              className="border rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
            >
              {recipe.photo_url && (
                <div className="h-48 bg-gray-200 relative">
                  <img 
                    src={recipe.photo_url} 
                    alt={recipe.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      console.log('Failed to load recipe image:', recipe.photo_url)
                      e.currentTarget.style.display = 'none'
                    }}
                  />
                  {recipe.is_favorite && (
                    <Star className="absolute top-2 right-2 w-6 h-6 text-yellow-400 fill-yellow-400" />
                  )}
                </div>
              )}
              
              <div className="p-4">
                <h3 className="font-semibold text-lg mb-1">{recipe.name}</h3>
                
                {recipe.description && (
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                    {recipe.description}
                  </p>
                )}
                
                <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                  {recipe.total_time_minutes && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {formatTime(recipe.total_time_minutes)}
                    </span>
                  )}
                  
                  {recipe.servings && (
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {recipe.servings}
                    </span>
                  )}
                  
                  {recipe.avg_rating && (
                    <span className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      {recipe.avg_rating.toFixed(1)}
                    </span>
                  )}
                </div>

                {recipe.tags && recipe.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {recipe.tags.slice(0, 3).map(tag => (
                      <span 
                        key={tag}
                        className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                    {recipe.tags.length > 3 && (
                      <span className="px-2 py-1 text-gray-500 text-xs">
                        +{recipe.tags.length - 3}
                      </span>
                    )}
                  </div>
                )}
                
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setViewingRecipeId(recipe.id)
                      setShowViewer(true)
                    }}
                    className="flex-1 px-3 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                  >
                    View
                  </button>
                  
                  <button
                    onClick={() => {
                      setEditingRecipeId(recipe.id)
                      setShowEditor(true)
                    }}
                    className="p-1.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={() => handleToggleFavorite(recipe.id)}
                    className={`p-1.5 rounded ${
                      recipe.is_favorite 
                        ? 'bg-yellow-100 text-yellow-600' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Star className={`w-4 h-4 ${recipe.is_favorite ? 'fill-current' : ''}`} />
                  </button>
                  
                  <button
                    onClick={() => handleLinkToMeal(recipe.id)}
                    className="p-1.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                    title="Link to meal option"
                  >
                    <Link className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={() => handleDelete(recipe.id)}
                    className="p-1.5 bg-red-100 text-red-600 rounded hover:bg-red-200"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}